import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import MHeader from "@/components/ui/organisms/MHeader";
import { getMemberInfo } from "@/hooks/useMembers";
import { DropdownOption } from "@/components/ui/atoms/MOptionsMenu";
import ReminderCard from "./ReminderCard";
import { useReminders } from "@/hooks/useReminders";
import {
  BaseReminders,
  Reminders,
  ReminderSubType,
  ReminderType,
} from "@/types/reminders";
import {
  IconSearch,
  IconEdit,
  IconCaretDownFilled,
  IconInfoSquare,
  IconCircleX,
  IconPillFilled,
  IconBellX,
  IconCalendar,
  IconShieldHeart,
  IconVaccine,
  IconPaw,
  IconRefresh,
  IconBell,
  IconActivityHeartbeat,
} from "@tabler/icons-react";
import styles from "./reminder.module.css";
import MDialog from "@/components/ui/atoms/MDialog";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import MLoader from "@/components/ui/atoms/MLoader";
import { useNavigate } from "react-router-dom";
import { vaccinationsService } from "@/services/vaccinations.service";
import usePetCare from "@/hooks/usePetCare";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setSelectedMember, clearSelectedMember } from "@/store/authSlice";
import { useManagedMembersContext } from "@/context/ManagedMembersContext";
import { updateNavigationSourceMemberId } from "@/utils/navigationSource";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MInfiniteScroll from "@/components/ui/atoms/MInfiniteScroll";
import { getEmphasizedText, getTertiaryText } from "./remindersHelper";
import ReminderViewModal from "./ReminderViewModal";
import { MTourTrigger } from "@/components/ui/organisms/MTourTrigger";
import { MHelpIcon } from "@/components/ui/atoms/MHelpIcon";
import { useNavigationSource } from "@/hooks/useNavigationSource";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

/**
 * RemindersListing component
 * Displays a list of reminders with search, filter, and CRUD operations
 */
interface RemindersListingProps {
  onAddReminder: () => void;
}

const RemindersListing: React.FC<RemindersListingProps> = ({
  onAddReminder,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const tourTriggerRef = React.useRef<any>(null);
  const memberInfo = getMemberInfo();
  const { navigateBack, hasNavigationSource } = useNavigationSource();
  const memberId = memberInfo.id || "";
  // The actual logged-in member, ignoring whatever `selectedMember` may
  // already be set to — needed to tell "my own reminder" apart from a
  // family member's reminder regardless of how this screen was entered.
  const loggedInMemberId = getMemberInfo(true).id || "";
  const { managedMembers, fetchManagedMembers } = useManagedMembersContext();
  // Get member name as requested for personalized messages
  const {
    reminders,
    loading,
    fetchMyReminders: fetchReminders,
    fetchMoreMyReminders: fetchMoreReminders,
    deleteReminder,
    category,
    setCategory,
    searchTerm,
    setSearchTerm,
    hasMoreMine: hasMore,
    isLoadingMoreMine: isLoadingMore,
  } = useReminders(memberId);
  const [inputValue, setInputValue] = useState(searchTerm);
  const [selectedCategory, setSelectedCategory] = useState<ReminderType>(
    ReminderType.ALL
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedReminderId, setSelectedReminderId] = useState<string | null>(
    null
  );
  const [selectedReminderMemberId, setSelectedReminderMemberId] = useState<
    string | undefined
  >(undefined);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [viewingReminder, setViewingReminder] = useState<Reminders | null>(
    null
  );
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const { getPetCare } = usePetCare(memberId);
  const [petCareDataMap, setPetCareDataMap] = useState<
    Record<
      string,
      { frequencyType: string; frequency?: number; nextDueDate?: Date }
    >
  >({});

  // Fetch reminders when component mounts or member changes
  // Single useEffect to handle both memberId and category changes
  useEffect(() => {
    if (memberId) {
      (async () => {
        setInitialLoadDone(false);
        await fetchReminders();
        setInitialLoadDone(true);
      })();
    }
  }, [memberId, category, fetchReminders]);

  useEffect(() => {
    const fetchPetCareData = async () => {
      const petCareReminders = reminders.filter(
        (r) => r.type === ReminderType.PET_CARE
      );
      const newPetCareDataMap: Record<
        string,
        { frequencyType: string; frequency?: number }
      > = {};

      for (const reminder of petCareReminders) {
        if (reminder.sourceId && !petCareDataMap[reminder.sourceId]) {
          try {
            const petCare = await getPetCare(reminder.sourceId);
            if (petCare) {
              newPetCareDataMap[reminder.sourceId] = {
                frequencyType: petCare.frequencyType,
                frequency: petCare.frequency,
                nextDueDate: petCare.nextDueDate,
              };
            }
          } catch (error) {
            console.error("Error fetching pet care data:", error);
          }
        }
      }

      if (Object.keys(newPetCareDataMap).length > 0) {
        setPetCareDataMap((prev) => ({ ...prev, ...newPetCareDataMap }));
      }
    };

    if (reminders.length > 0) {
      fetchPetCareData();
    }
  }, [reminders, getPetCare]);

  // Handle search when user clicks search icon
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // Handle category selection
  const handleCategorySelect = (categorySelected: ReminderType) => {
    setSelectedCategory(categorySelected);
    setShowFilterDrawer(false);
    setCategory(categorySelected);
    // No need to call fetchReminders() here as the useEffect will handle it
  };

  // Generate actions for each reminder item
  const getReminderActions = (reminder: BaseReminders): DropdownOption[] => {
    return [
      {
        id: "edit",
        label: t("common.edit"),
        icon: <IconEdit size={20} />,
        onClick: () => handleEditReminder(reminder),
        isVisible: true,
      },
      {
        id: "delete",
        label: t("common.delete"),
        icon: <IconCircleX size={20} className="text-semantic-icon-error" />,
        onClick: () => showDeleteReminderDialog(reminder.id, reminder.memberId),
        isVisible: true,
        className: "text-semantic-icon-error",
      },
    ];
  };

  const getFilterItems = (): { value: string; label: string }[] => {
    // This listing spans reminders across the whole family (not just the
    // current member context), so every type should be filterable regardless
    // of the current member's own derivedMemberType.
    const reminderItems = [
      ReminderType.ALL,
      ReminderType.MEDICATION,
      ReminderType.APPOINTMENT,
      ReminderType.INSURANCE,
      ReminderType.VACCINATION,
      ReminderType.VITALS,
      ReminderType.PET_CARE,
    ];
    const finalRenderItems = reminderItems.map((item) => ({
      value: item,
      label: t(`reminders.filterTypes.${item}`),
    }));
    return finalRenderItems;
  };

  const getIcon = (reminder: BaseReminders) => {
    switch (reminder.type) {
      case ReminderType.MEDICATION:
        return <IconPillFilled size={24} />;
      case ReminderType.APPOINTMENT:
        return <IconCalendar size={24} />;
      case ReminderType.INSURANCE:
        return <IconShieldHeart size={24} />;
      case ReminderType.VACCINATION:
        return <IconVaccine size={24} />;
      case ReminderType.PET_CARE:
        return <IconPaw size={24} />;
      case ReminderType.VITALS:
        return <IconActivityHeartbeat size={24} />;
      default:
        return <IconInfoSquare size={24} />;
    }
  };

  // The medication/appointment/insurance/pet-care edit pages all resolve
  // their working member via getMemberInfo(), which reads Redux
  // auth.selectedMember — the same mechanism the family profile picker
  // uses elsewhere in the app. Since this listing now spans reminders for
  // multiple family members, editing one created for someone other than
  // the logged-in member must switch that selection first, so the edit
  // page picks up the right member on its own next render.
  const switchToReminderMember = async (reminder: BaseReminders) => {
    const targetMemberId = reminder.memberId;
    if (!targetMemberId || targetMemberId === loggedInMemberId) {
      dispatch(clearSelectedMember());
      return;
    }
    const members =
      managedMembers.length > 0 ? managedMembers : await fetchManagedMembers();
    const targetMember = members.find((m) => m.id === targetMemberId);
    if (!targetMember) {
      console.error(
        `Could not find managed member ${targetMemberId} for reminder ${reminder.id}`
      );
      return;
    }
    dispatch(setSelectedMember(targetMember));
    updateNavigationSourceMemberId(targetMemberId);
  };

  // Handle edit reminder
  const handleEditReminder = async (reminder: BaseReminders) => {
    // Navigate to edit page or open edit modal
    setSelectedReminderId(reminder.id);
    await switchToReminderMember(reminder);
    switch (reminder.type) {
      case ReminderType.MEDICATION:
        navigate(`/reminders/medication`, { state: { reminder } });
        break;
      case ReminderType.APPOINTMENT:
        navigate(`/reminders/appointment`, { state: { reminder } });
        break;
      case ReminderType.INSURANCE:
        navigate(`/reminders/insurance`, { state: { reminder } });
        break;
      case ReminderType.VACCINATION:
        try {
          // Fetch with the reminder's own target member directly — the
          // redux selectedMember switch above only takes effect once the
          // *next* page renders, so it can't be relied on for this
          // synchronous fetch.
          const vaccination = await vaccinationsService.getVaccination(
            reminder.memberId || memberId,
            reminder.sourceId
          );
          navigate(`/reminders/add-vaccine`, { state: { vaccination } });
        } catch (error) {
          showToast({
            message: t("reminders.errors.errorFetchingVaccination"),
            variant: "error",
          });
        }
        break;
      case ReminderType.PET_CARE:
        navigate("/reminders/pet-care");
        break;
      case ReminderType.VITALS:
        navigate("/reminders/vitals", { state: { reminder } });
        break;
      default:
        break;
    }
  };

  const handleEditReminderDone = () => {
    setShowEditDrawer(false);
    setSelectedReminderId(null);
    fetchReminders();
  };

  const showDeleteReminderDialog = (
    reminderId: string,
    reminderMemberId?: string
  ) => {
    setSelectedReminderId(reminderId);
    setSelectedReminderMemberId(reminderMemberId);
    setShowDeleteDialog(true);

    // Track delete click
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_HOME,
      action: "delete_click",
    });
  };

  // Handle delete reminder
  const handleDeleteReminder = async () => {
    // show delete confirmation dialog — pass the reminder's own target
    // member (not the logged-in member) since the listing now spans
    // reminders created for other family members too
    await deleteReminder(selectedReminderId, selectedReminderMemberId);
    setSelectedReminderId(null);
    setSelectedReminderMemberId(undefined);
    setShowDeleteDialog(false);

    // Track delete confirmation
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_HOME,
      action: "delete_confirm",
    });

    fetchReminders();
  };

  if (loading) {
    return <MLoader />;
  }

  return (
    <>
      {!hasNavigationSource() && (
        <div className={styles.headerHelpIcon} data-wizard-help-icon>
          <MHelpIcon onClick={() => tourTriggerRef.current?.open()} />
        </div>
      )}
      {/* Header */}
      <MHeader
        module="reminders_listing"
        memberInfo={memberInfo}
        headerLabel="Reminders"
        titleSize="lg"
        className={styles.headerCurve}
        onAdd={onAddReminder}
        // showSearch={selectedCategory !== ReminderType.ALL}
        searchPlaceholder={t("reminders.listing.searchPlaceholder")}
        value={inputValue}
        onValueChange={setInputValue}
        onSearch={handleSearch}
        onClear={() => {
          setInputValue("");
          setSearchTerm("");
        }}
        showBackButton={hasNavigationSource()}
        onBack={navigateBack}
        description={
          "Set reminders for anyone in the family. The notifications come only to you."
        }
        learnMoreContent={
          <>
            When you set a reminder—no matter who it is for—it will appear only
            in your profile, and the alert will be sent only to your phone.
            <br /> <br /> For example, if you set a reminder for your father's
            blood test, the notification will come to your device at the date
            and time you've set.
          </>
        }
      />

      {loading ? (
        <MLoader />
      ) : (
        <>
          {initialLoadDone && reminders.length === 0 && searchTerm && (
            <>
              <MSpacer mt={24} />
              <MEmptyCard
                primaryTitle={t("reminders.searchEmpty")}
                secondaryTitle={t("reminders.searchEmptyDescription")}
                icon={<IconSearch />}
              />
            </>
          )}

          {initialLoadDone &&
            reminders.length === 0 &&
            !searchTerm &&
            selectedCategory === ReminderType.ALL && (
              <>
                <MSpacer mt={24} />
                <MEmptyCard
                  primaryTitle={t("reminders.listing.noRemindersSet")}
                  secondaryTitle={t("reminders.listing.noRemindersHelp")}
                  icon={<IconBellX />}
                  primaryCTA={{
                    text: t("reminders.listing.addReminder"),
                    onClick: onAddReminder,
                  }}
                />
              </>
            )}

          {/* Reminders list */}
          <div>
            <MSpacer mt={24} />
            <div className={styles.listingContainer}>
              {(reminders.length > 0 ||
                selectedCategory !== ReminderType.ALL) && (
                <button
                  type="button"
                  className={styles.filterDropdown}
                  onClick={() => setShowFilterDrawer(true)}
                  aria-label={t("reminders.filterTitle")}
                >
                  <span>{t(`reminders.filterTypes.${selectedCategory}`)}</span>
                  <IconCaretDownFilled size={16} />
                </button>
              )}

              {initialLoadDone &&
                reminders.length === 0 &&
                !searchTerm &&
                selectedCategory !== ReminderType.ALL && (
                  <div className="w-full">
                    <MEmptyCard
                      primaryTitle={t("reminders.categoryEmpty")}
                      icon={<IconBellX />}
                    />
                  </div>
                )}

              {reminders.length > 0 && (
                <MInfiniteScroll
                  data={reminders}
                  hasMore={hasMore}
                  isLoading={isLoadingMore}
                  onLoadMore={fetchMoreReminders}
                  className={styles.cardsList}
                  gap={0}
                  renderItem={(reminder) => {
                    const isTherapy =
                      reminder.type === ReminderType.APPOINTMENT &&
                      reminder.subType === ReminderSubType.THERAPY;
                    const petCareData =
                      reminder.type === ReminderType.PET_CARE &&
                      reminder.sourceId
                        ? petCareDataMap[reminder.sourceId]
                        : undefined;
                    const hasFrequency =
                      reminder.type !== ReminderType.APPOINTMENT || isTherapy;

                    return (
                      <ReminderCard
                        key={reminder.id}
                        icon={getIcon(reminder)}
                        badgeLabel={
                          reminder.onBehalf && reminder.name
                            ? t("reminders.listing.familyBadge", {
                                name: reminder.name,
                              })
                            : t("reminders.listing.yours")
                        }
                        isOwn={!reminder.onBehalf}
                        title={reminder.reminderFor}
                        onTitleClick={() => setViewingReminder(reminder)}
                        secondaryText={getEmphasizedText(
                          reminder,
                          t,
                          petCareData
                        )}
                        secondaryIcon={
                          hasFrequency ? (
                            <IconRefresh size={16} color="#2A2A2A" />
                          ) : undefined
                        }
                        tertiaryText={getTertiaryText(
                          reminder,
                          petCareData?.nextDueDate
                        )}
                        tertiaryIcon={<IconBell size={16} />}
                        actions={getReminderActions(reminder)}
                      />
                    );
                  }}
                />
              )}
            </div>
          </div>

          {/* Delete reminder modal */}
          <MDialog
            isOpen={showDeleteDialog}
            onClose={() => setShowDeleteDialog(false)}
            title={t("reminders.deleteReminder.title")}
            description={t("reminders.deleteReminder.description")}
            primaryCtaText={t("common.yes")}
            onPrimaryCta={handleDeleteReminder}
            secondaryCtaText={t("common.no")}
            onSecondaryCta={() => setShowDeleteDialog(false)}
            showCloseButton={true}
          />

          {/* Filter drawer */}
          <MDropDownDrawer
            open={showFilterDrawer}
            onClose={() => setShowFilterDrawer(false)}
            title={t("reminders.filterTitle")}
            initialValue={selectedCategory}
            items={getFilterItems()}
            onSelect={handleCategorySelect}
            variant="single"
          />

          <ReminderViewModal
            reminder={viewingReminder}
            icon={viewingReminder ? getIcon(viewingReminder) : undefined}
            isOpen={!!viewingReminder}
            onClose={() => setViewingReminder(null)}
          />
        </>
      )}

      <MTourTrigger ref={tourTriggerRef} page="reminders" />
    </>
  );
};

export default RemindersListing;
